import React, { useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useTheme } from 'styled-components'
import { Tonality } from '@dsny/dsny-component-library'
import {
  Keyboardarrowright,
  Add,
  Keyboardarrowdown
} from '@dsny/dsny-component-library/icons'
import { generateFilterOptions } from '../utils'
import FilterDropdown from 'components/Filtering/FilterDropdown/FilterDropdown'
import styles from '../../Dropdowns/Dropdowns.module.scss'
import OptionActions from 'reducers/Options'
import { trackEvent } from 'utils/segmentService'
import {
  TRACKING_EVENTS,
  FILTER_ACTIONS,
  PLATFORMS
} from '../../../constants/tracking'
import { REQUEST_NEW_LIST_URL } from 'utils/constants'
import {
  FilterWrapper,
  StyledDropdownMenuSubContent
} from './FiltersMenu.styles'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { checkFlag } from '@dsny/dsny-feature-flag-library'
import { useFeatureFlags } from 'contexts/FeatureFlagsContext'

export default function FiltersMenu() {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { ready: featureFlagsReady } = useFeatureFlags()
  const showAIFilter = featureFlagsReady ? checkFlag('showAIFilter') : false
  const isMobile = useMobileMediaQuery()
  const {
    genres,
    markets,
    packages,
    release_types,
    versions,
    moods,
    instruments,
    activeFilters
    // additionalFilterSettings
  } = useSelector((state) => state.options)

  // Track filter interactions
  const trackFilterEvent = (action, filterType, filterLabel) => {
    const normalizedLabel = filterLabel ? String(filterLabel) : ''
    const basePayload = {
      action,
      filter_type: filterType ? String(filterType) : '',
      filter_label: normalizedLabel,
      platform: PLATFORMS.DESKTOP,
      total_active_filters: String(activeFilters.length)
    }

    trackEvent(TRACKING_EVENTS.FILTER_INTERACTION, {
      ...basePayload,
      statsigCustom: {
        ...basePayload
      }
    })
  }

  const handleFilterToggle = (type, items) => {
    return (e, _, value) => {
      e.stopPropagation()
      const filterObj = items?.find((item) => item.id === value)
      if (!filterObj) return

      // Check if filter is currently selected to determine action
      const isCurrentlySelected = activeFilters.some(
        (f) => f.type === type && f.id === filterObj.id
      )

      // Track the event
      trackFilterEvent(
        isCurrentlySelected ? FILTER_ACTIONS.REMOVE : FILTER_ACTIONS.SELECT,
        type,
        filterObj.label || filterObj.name || filterObj.id
      )

      dispatch(
        OptionActions.toggleFilter({
          id: filterObj.id,
          label: filterObj.label || filterObj.name || filterObj.id,
          type
        })
      )
    }
  }

  // Single select handler for mood and instrument (radio button behavior)
  const handleSingleSelectToggle = (type, items) => {
    return (e, _, value) => {
      e.stopPropagation()
      const filterObj = items.find((item) => item.id === value)
      if (!filterObj) return

      // Check if this filter is already selected
      const isCurrentlySelected = activeFilters.some(
        (f) => f.type === type && f.id === filterObj.id
      )

      // Track the event
      trackFilterEvent(
        isCurrentlySelected ? FILTER_ACTIONS.REMOVE : FILTER_ACTIONS.SELECT,
        type,
        filterObj.label || filterObj.name || filterObj.id
      )

      if (isCurrentlySelected) {
        // If already selected, remove it (deselect) by toggling
        dispatch(
          OptionActions.toggleFilter({
            id: filterObj.id,
            label: filterObj.label || filterObj.name || filterObj.id,
            type
          })
        )
      } else {
        // For single select, remove all filters of this type and add the new one
        const filtersWithoutThisType = activeFilters.filter(
          (f) => f.type !== type
        )
        const newFilters = [
          ...filtersWithoutThisType,
          {
            id: filterObj.id,
            label: filterObj.label || filterObj.name || filterObj.id,
            type
          }
        ]

        dispatch(OptionActions.setFilters(newFilters))
      }
    }
  }

  // Helper to get selected ids for each type from activeFilters
  const getSelectedIds = (type) =>
    activeFilters.filter((f) => f.type === type).map((f) => f.id)

  const genresFilters = useMemo(
    () => generateFilterOptions(genres, getSelectedIds('genre')),
    [genres, activeFilters]
  )

  const moodsFilters = useMemo(
    () => generateFilterOptions(moods, getSelectedIds('mood')),
    [moods, activeFilters]
  )

  const instrumentsFilters = useMemo(
    () => generateFilterOptions(instruments, getSelectedIds('instrument')),
    [instruments, activeFilters]
  )

  const marketsFilters = useMemo(
    () => generateFilterOptions(markets, getSelectedIds('market')),
    [markets, activeFilters]
  )

  const versionsFilters = useMemo(
    () => generateFilterOptions(versions, getSelectedIds('version')),
    [versions, activeFilters]
  )

  const packagesFilters = useMemo(
    () => generateFilterOptions(packages, getSelectedIds('package')),
    [packages, activeFilters]
  )

  const releaseTypesFilters = useMemo(
    () => generateFilterOptions(release_types, getSelectedIds('release_type')),
    [release_types, activeFilters]
  )

  return (
    <FilterWrapper $isMobile={isMobile}>
      <FilterDropdown
        label={t('home.filter-genres-title')}
        filters={genresFilters.sort((a, b) => b.isSelected - a.isSelected)}
        onFilterSelect={handleFilterToggle('genre', genres)}
      />
      {showAIFilter && (
        <FilterDropdown
          label={t('home.filter-mood-title')}
          filters={moodsFilters}
          onFilterSelect={handleSingleSelectToggle('mood', moods)}
          singleSelect={true}
        />
      )}

      {showAIFilter && (
        <FilterDropdown
          label={t('home.filter-instrument-title')}
          filters={instrumentsFilters}
          onFilterSelect={handleSingleSelectToggle('instrument', instruments)}
          singleSelect={true}
        />
      )}

      <FilterDropdown
        label={t('home.filter-release-type-title')}
        filters={releaseTypesFilters}
        onFilterSelect={handleFilterToggle('release_type', release_types)}
      />
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0
            }}
          >
            <Tonality.Pill
              variant="tonal"
              mode="dark"
              label={t('home.filter-more-filters-title') || 'More filters'}
              iconPosition="tailing"
              icon={
                <Keyboardarrowdown
                  color={theme.colors.white0}
                  style={{ fontSize: 14 }}
                />
              }
            />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            side="bottom"
            align="start"
            style={{
              background: theme.colors.neutralW20,
              color: theme.colors.neutralW100,
              padding: '5px 0',
              border: `0.5px solid ${theme.colors.neutralW30}`,
              width: 128
            }}
            className={styles.DropdownMenuContent}
            sideOffset={5}
            avoidCollisions
            collisionPadding={150}
          >
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger className={styles.DropdownMenuItem}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  {t('home.filter-lists-title')}
                  <Keyboardarrowright
                    width={20}
                    height={20}
                    color={theme.colors.neutralW100}
                  />
                </span>
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <StyledDropdownMenuSubContent
                  className={styles.DropdownMenuContent}
                  sideOffset={2}
                  alignOffset={-5}
                >
                  {packagesFilters.map((option, index) => (
                    <DropdownMenu.Item
                      key={index}
                      className={styles.DropdownMenuItem}
                      onSelect={(e) =>
                        handleFilterToggle('package', packages)(
                          e,
                          option.label,
                          option.value
                        )
                      }
                    >
                      <Tonality.Checkbox
                        checked={option.isSelected}
                        style={{
                          marginRight: 8,
                          background: option.isSelected
                            ? theme.colors.interact70
                            : theme.colors.neutralW20
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleFilterToggle('package', packages)(
                            e,
                            option.label,
                            option.value
                          )
                        }}
                      />
                      {option.label}
                    </DropdownMenu.Item>
                  ))}
                  <DropdownMenu.Separator
                    style={{
                      height: 1,
                      background: theme.colors.neutralW30
                    }}
                  />
                  <DropdownMenu.Item
                    className={styles.DropdownMenuItem}
                    onClick={() => window.open(REQUEST_NEW_LIST_URL, '_blank')}
                  >
                    {t('home.filter-request-new-list')}
                    <div className={styles.RightSlot}>
                      <Add />
                    </div>
                  </DropdownMenu.Item>
                </StyledDropdownMenuSubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger className={styles.DropdownMenuItem}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  {t('home.filter-markets-title')}
                  <Keyboardarrowright
                    width={20}
                    height={20}
                    color={theme.colors.neutralW100}
                  />
                </span>
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <StyledDropdownMenuSubContent
                  className={styles.DropdownMenuContent}
                  sideOffset={2}
                  alignOffset={-5}
                >
                  {marketsFilters.map((option, index) => (
                    <DropdownMenu.Item
                      key={index}
                      className={styles.DropdownMenuItem}
                      onSelect={(e) =>
                        handleFilterToggle('market', markets)(
                          e,
                          option.label,
                          option.value
                        )
                      }
                    >
                      <Tonality.Checkbox
                        checked={option.isSelected}
                        style={{
                          marginRight: 8,
                          background: option.isSelected
                            ? theme.colors.interact70
                            : theme.colors.neutralW20
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleFilterToggle('market', markets)(
                            e,
                            option.label,
                            option.value
                          )
                        }}
                      />
                      {option.label}
                    </DropdownMenu.Item>
                  ))}
                </StyledDropdownMenuSubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger className={styles.DropdownMenuItem}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  {t('home.filter-song-version-title')}
                  <Keyboardarrowright
                    width={20}
                    height={20}
                    color={theme.colors.neutralW100}
                  />
                </span>
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <StyledDropdownMenuSubContent
                  className={styles.DropdownMenuContent}
                  sideOffset={2}
                  alignOffset={-5}
                >
                  {versionsFilters.map((option, index) => (
                    <DropdownMenu.Item
                      key={index}
                      className={styles.DropdownMenuItem}
                      onSelect={(e) =>
                        handleFilterToggle('version', versions)(
                          e,
                          option.label,
                          option.value
                        )
                      }
                    >
                      <Tonality.Checkbox
                        checked={option.isSelected}
                        style={{
                          marginRight: 8,
                          background: option.isSelected
                            ? theme.colors.interact70
                            : theme.colors.neutralW20
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleFilterToggle('version', versions)(
                            e,
                            option.label,
                            option.value
                          )
                        }}
                      />
                      {option.label}
                    </DropdownMenu.Item>
                  ))}
                </StyledDropdownMenuSubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </FilterWrapper>
  )
}
